// Lettura del verbale pubblicato, condivisa da associati e admin: intestazione
// dell'evento, foglio in sola lettura, allegati e «Scarica PDF». Le pagine
// passano i dati, il modo di scaricare il PDF e i dettagli propri (admin).
import React, { useState } from 'react';
import { Download, FileText, Loader2, Paperclip } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { messaggioErrorePdf, nomeFilePdfVerbale, salvaPdf } from '../../utils/scaricaPdf';
import type { AllegatoLettura, VerbaleLetturaDati } from '../../services/verbaliLettoriService';

interface VerbaleLetturaProps {
  dati: VerbaleLetturaDati;
  onScaricaPdf: () => Promise<Blob>;
  dettagli?: React.ReactNode;
}

const PATH_ALLEGATO_VALIDO = /^\/uploads\/verbali\/[0-9a-f]{24}\/[^/]+$/;

const formatoData = (iso: string | null | undefined, conOra = false): string => {
  if (!iso) return '';
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '';
  return data.toLocaleString('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Rome',
    ...(conOra ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
};

/** Link all'allegato solo con il path valido (`/uploads/verbali/<eventId>/<file>`), altrimenti null. */
export const urlAllegato = (path: string): string | null => {
  if (!PATH_ALLEGATO_VALIDO.test(path || '')) return null;
  const base = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '').replace(/\/api$/, '');
  return `${base}${path}`;
};

/** Nome mostrato: il nome originale (o l'ultimo pezzo del path) senza il prefisso `<timestamp>-` del caricamento. */
const nomeAllegato = (a: AllegatoLettura): string => {
  const nome = (a.filename || (a.path || '').split('/').pop() || '').trim();
  return nome.replace(/^\d{10,}-/, '') || 'allegato';
};

/** Un'immagine con `src` non vuoto (stessa `RE_IMMAGINE` di `backend/utils/verbaleLettura.js`). */
const RE_IMMAGINE = /<img\b[^>]*\ssrc\s*=\s*["']?[^"'\s>]/i;

/**
 * true se l'HTML (già sanificato) ha testo visibile o un'immagine con `src`: regola unica dei
 * lettori, uguale a `testoVisibile` di `backend/utils/verbaleLettura.js` (le tabelle contano per
 * il loro testo).
 */
export const haTestoVisibile = (html: string): boolean => {
  if (!html) return false;
  if (RE_IMMAGINE.test(html)) return true;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;|&#160;|&#xa0;/gi, ' ').trim().length > 0;
};

const VerbaleLettura: React.FC<VerbaleLetturaProps> = ({ dati, onScaricaPdf, dettagli }) => {
  const toast = useToast();
  const [pdfInCorso, setPdfInCorso] = useState(false);
  const { evento, verbale } = dati;

  // Il testo lo scrive un rappresentante: elenco di consentiti, non il solo stripDangerousHtml
  const contenuto = sanitizeHtml(verbale.contenuto || '');
  const conTesto = haTestoVisibile(contenuto);
  const allegati = verbale.allegati || [];

  const orario = [evento.oraInizio, evento.oraFine].filter(Boolean).join('–');
  const rigaEvento = [evento.tipologia?.nome, formatoData(evento.dataEvento), orario, evento.luogo]
    .filter(Boolean)
    .join(' · ');

  const pubblicato = formatoData(verbale.pubblicatoAt, true);
  const tempoPubblicazione = verbale.pubblicatoAt ? new Date(verbale.pubblicatoAt).getTime() : NaN;
  const tempoModifica = verbale.ultimaModifica ? new Date(verbale.ultimaModifica).getTime() : NaN;
  const aggiornato =
    !Number.isNaN(tempoPubblicazione) && !Number.isNaN(tempoModifica) && tempoModifica > tempoPubblicazione
      ? formatoData(verbale.ultimaModifica, true)
      : '';

  const scarica = async () => {
    if (pdfInCorso) return;
    setPdfInCorso(true);
    try {
      const blob = await onScaricaPdf();
      salvaPdf(blob, nomeFilePdfVerbale(evento.nome));
    } catch (err) {
      toast.error(await messaggioErrorePdf(err, 'Errore nella preparazione del PDF'));
    } finally {
      setPdfInCorso(false);
    }
  };

  return (
    <article className="space-y-4">
      <header className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-400">
              <FileText className="h-3.5 w-3.5" aria-hidden="true" />
              Verbale
            </p>
            <h1 className="mt-1 break-words text-lg font-semibold text-slate-900 dark:text-white">{evento.nome}</h1>
            {rigaEvento && <p className="mt-1 text-sm text-slate-600 dark:text-gray-300">{rigaEvento}</p>}
            {(pubblicato || aggiornato) && (
              <p className="mt-1 text-xs text-slate-500 dark:text-gray-400">
                {pubblicato && <span>Pubblicato il {pubblicato}</span>}
                {pubblicato && aggiornato && <span> · </span>}
                {aggiornato && <span>Aggiornato il {aggiornato}</span>}
              </p>
            )}
            {dettagli && <div className="mt-2 text-sm text-slate-700 dark:text-gray-300">{dettagli}</div>}
          </div>
          <button
            type="button"
            onClick={scarica}
            disabled={pdfInCorso}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-blue-500 dark:hover:bg-blue-600"
          >
            {pdfInCorso ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Download className="h-4 w-4" aria-hidden="true" />
            )}
            {pdfInCorso ? 'Preparazione PDF…' : 'Scarica PDF'}
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-3xl rounded-xl border border-slate-200 bg-white px-5 py-6 shadow-sm dark:border-gray-700 dark:bg-gray-900 sm:px-10 sm:py-10">
        {conTesto ? (
          <div
            className="prose prose-sm prose-slate max-w-none break-words font-serif text-base leading-relaxed prose-headings:text-base prose-headings:font-semibold text-slate-800 dark:prose-invert dark:text-gray-200 [&_img]:h-auto [&_img]:max-w-full [&_table]:w-full"
            dangerouslySetInnerHTML={{ __html: contenuto }}
          />
        ) : (
          <p className="text-sm italic text-slate-500 dark:text-gray-400">Il verbale non contiene testo.</p>
        )}
      </div>

      {allegati.length > 0 && (
        <section className="mx-auto max-w-3xl rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 dark:text-white">
            <Paperclip className="h-4 w-4" aria-hidden="true" />
            Allegati ({allegati.length})
          </h2>
          <ul className="mt-2 space-y-1.5">
            {allegati.map(a => {
              const url = urlAllegato(a.path);
              const nome = nomeAllegato(a);
              return (
                <li key={a._id || a.path} className="flex items-center gap-2 text-sm">
                  <FileText className="h-4 w-4 shrink-0 text-slate-400 dark:text-gray-500" aria-hidden="true" />
                  {url ? (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="min-w-0 truncate text-blue-700 underline-offset-2 hover:underline dark:text-blue-400"
                    >
                      {nome}
                    </a>
                  ) : (
                    <span className="min-w-0 truncate text-slate-700 dark:text-gray-300">{nome}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </article>
  );
};

export default VerbaleLettura;
